
import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { enUS, fr, es } from "date-fns/locale";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { SwingTendency } from "./SwingChartUtils";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface DriverPointsChartProps {
  tendencies: SwingTendency[];
  selectedLevel: string | null;
}

const DriverPointsChart: React.FC<DriverPointsChartProps> = ({ 
  tendencies,
  selectedLevel 
}) => {
  const { t, i18n } = useTranslation("training");
  const [viewMode, setViewMode] = useState<"individual" | "combined">("individual");
  
  // Get locale for date formatting
  const getLocale = () => {
    switch (i18n.language) {
      case 'fr': return fr;
      case 'es': return es;
      default: return enUS;
    }
  };
  
  // Filter tendencies to only include driver data
  const driverTendencies = tendencies.filter(t => t.club_type === "driver");
  
  // Filter tendencies based on selected level
  const filteredTendencies = selectedLevel 
    ? driverTendencies.filter(t => t.test_level === selectedLevel)
    : driverTendencies;
  
  // Sort tendencies by date (ascending)
  const sortedTendencies = [...filteredTendencies].sort(
    (a, b) => new Date(a.test_date).getTime() - new Date(b.test_date).getTime()
  );
  
  // Group tendencies by date and calculate total points for each date
  // This will solve the issue of multiple entries at the same time
  const groupedData = useMemo(() => {
    const dateMap = new Map();

    sortedTendencies.forEach(tendency => {
      const dateFormat = i18n.language === 'en' ? 'MM/dd/yyyy' : 'dd/MM/yyyy';
      const date = format(new Date(tendency.test_date), dateFormat, { locale: getLocale() });
      const level = tendency.test_level || 'unknown';
      const key = `${date}-${level}`;
      
      if (!dateMap.has(key)) {
        dateMap.set(key, {
          date,
          level,
          points: tendency.points || 0,
          count: 1,
          testName: "Driver - Précision"
        });
      } else {
        // If entry already exists, update the points (we'll take the highest value)
        const entry = dateMap.get(key);
        if (tendency.points && tendency.points > entry.points) {
          entry.points = tendency.points;
        }
      }
    });

    return Array.from(dateMap.values());
  }, [sortedTendencies]);
  
  // Format data for chart - ensure points is a number
  const chartData = groupedData.map(entry => ({
    date: entry.date,
    points: entry.points,
    level: entry.level,
    testName: entry.testName
  }));
  
  // Log for debugging
  console.log("Driver chart data:", chartData);
  
  if (chartData.length === 0) {
    return (
      <Card className="bg-gray-50">
        <CardContent className="p-8 text-center text-gray-500">
          Aucune donnée disponible pour afficher le graphique.
          <br />
          Complétez quelques exercices pour voir votre évolution.
        </CardContent>
      </Card>
    );
  }
  
  // Set fixed domain values to ensure chart is always visible
  const pointsDomain: [number, number] = [0, 50]; // Points typically between 0-50 for driver
  
  return (
    <Card>
      <div className="p-4 flex justify-between items-center border-b">
        <h4 className="text-sm font-medium">{t("driver.resultsEvolution")}</h4>
        <Tabs defaultValue="individual" value={viewMode} onValueChange={(value) => setViewMode(value as "individual" | "combined")}>
          <TabsList className="grid w-[240px] grid-cols-2">
            <TabsTrigger value="individual">{t('charts.tabs.individual', { ns: 'common' })}</TabsTrigger>
            <TabsTrigger value="combined">{t('charts.tabs.combined', { ns: 'common' })}</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <CardContent className="p-4">
        <div className="h-[250px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                tickMargin={8}
              />
              <YAxis
                orientation="left"
                domain={pointsDomain}
                tick={{ fontSize: 12 }}
                tickMargin={8}
                width={30}
                allowDecimals={false}
              />
              <Tooltip
                formatter={(value, name, props) => {
                  if (name === "points") {
                    return [`${value} pts (Niveau ${props.payload.level || "?"})`, "Points"];
                  }
                  return [value, name];
                }}
                labelFormatter={(label) => `Date: ${label}`}
              />
              <Line
                type="monotone"
                dataKey="points"
                name="points"
                stroke="#10b981"
                strokeWidth={2}
                dot={{ strokeWidth: 2, r: 4, fill: "#10b981" }}
                activeDot={{ strokeWidth: 0, r: 6, fill: "#10b981" }}
                isAnimationActive={true}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default DriverPointsChart;
